<template>
  <div
    v-if="!isChallenge"
    class="matches-page"
  >
    <header
      class="page-header d-flex align-center justify-space-between px-6"
    >
      <div class="header-left">
        <v-img
          :src="appLogoTitle"
          alt="Fanaty"
          max-width="140"
          contain
        ></v-img>
      </div>
      <div class="header-right">
        <v-btn
          text
          small
          :class="{ 'font-weight-bold': lang === 'en' }"
          @click="() => updateActiveLocale('en')"
        >
          ENGLISH
        </v-btn>
        <span class="mx-2">|</span>
        <v-btn
          text
          small
          :class="{ 'font-weight-bold': lang === 'es' }"
          @click="() => updateActiveLocale('es')"
        >
          ESPAÑOL
        </v-btn>
      </div>
    </header>

    <v-container
      fluid
      class="fill-height d-flex align-center justify-center matches-download-page"
    >
      <v-row
        align="center"
        justify="center"
        class="w-100"
      >
        <v-col
          cols="12"
          md="7"
          class="text-center"
        >
          <h1 class="display-1 font-weight-bold mb-4">
            {{ $t('matches.main') }}
          </h1>
          <p class="subtitle-1 mb-6">
            {{ $t('matches.secondary') }}
          </p>

          <v-row
            justify="center"
            class="mb-6"
          >
            <v-col cols="auto">
              <a
                :href="appStoreLink"
                target="_blank"
                rel="noopener"
                class="store-badge-link"
                :aria-label="lang === 'es' ? 'Descargar en App Store' : 'Download on the App Store'"
              >
                <v-img
                  src="/app-store.png"
                  alt="App Store"
                  max-width="170"
                  contain
                ></v-img>
              </a>
            </v-col>
            <v-col cols="auto">
              <a
                :href="playStoreLink"
                target="_blank"
                rel="noopener"
                class="store-badge-link"
                :aria-label="lang === 'es' ? 'Descargar en Google Play' : 'Download on Google Play'"
              >
                <v-img
                  src="/google-play.png"
                  alt="Google Play"
                  max-width="170"
                  contain
                ></v-img>
              </a>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          v-if="!$vuetify.breakpoint.smAndDown"
          cols="12"
          md="4"
          class="d-flex align-center justify-center"
        >
          <v-img
            src="/img_app.png"
            alt="img_app"
            class="app-image"
            contain
          ></v-img>
        </v-col>
      </v-row>
      <!-- session image fixed at bottom -->
      <!-- <v-img
        src="/session.svg"
        alt="session"
        class="session-image"
        contain
      ></v-img> -->
    </v-container>

    <!-- footer -->
    <v-footer
      class="footer"
      padless
    >
      <v-container>
        <v-row justify="center">
          <v-col
            cols="12"
            class="text-center"
          >
            <span class="footer-label">© FANATY {{ year }}</span>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </div>
  <game v-else />
</template>

<script>
import { ref, onBeforeMount } from '@vue/composition-api'
import { getMatchGameOpenCode } from '@api'
import {
  isAndroid, isIOS, openTab, isDev, useRouter,
} from '@core/utils'
import themeConfig from '@themeConfig'
import { loadLanguageAsync } from '@/plugins/i18n'
import Game from '@/views/game/Game.vue'

export default {
  name: 'MatchesDownload',
  components: {
    Game,
  },
  setup() {
    const { route } = useRouter()

    const lang = ref('es')
    const matchCode = ref(null)
    const matchId = ref(null)
    const isChallenge = ref(false)

    const appStoreLink = ref('https://apps.apple.com/us/app/fanaty/id1461051554')
    const playStoreLink = ref('https://play.google.com/store/apps/details?id=com.fanaty')

    const updateActiveLocale = locale => {
      lang.value = locale
      loadLanguageAsync(locale)
    }

    const onPrimaryClick = () => {
      window.open(playStoreLink.value, '_blank')
    }

    onBeforeMount(async () => {
      const deepLinkMatch = 'fanaty://match_detail/'
      const deepLinkMatchIOS = `${isDev.value ? 'https://dev-console.fanaty.com' : 'https://console.fanaty.com'}/match_detail/`
      const { code } = route.value.params
      matchCode.value = code
      const response = await getMatchGameOpenCode(code)
      if (response && response.ok) {
        isChallenge.value = response.data.is_challenge === 'Y'
        matchId.value = response.data.match_id
        if (isAndroid() || isIOS()) {
          const storeDelay = 2000 // 2 segundos

          if (isIOS()) {
            // Intentar abrir el universal link con timestamp para evitar caché
            const timestamp = Date.now()
            openTab(`${deepLinkMatchIOS}${matchId.value}?t=${timestamp}`)

            // Después de 2 segundos, redirigir a App Store
            // Si la app abrió, el usuario ya no está aquí
            // Si la app no abrió, el usuario va a descargarla
            setTimeout(() => {
              window.location = appStoreLink.value
            }, storeDelay)
          } else {
            // Android: intentar abrir deep link
            window.location = `${deepLinkMatch}${matchId.value}`

            // Después de 2 segundos, redirigir a Play Store
            setTimeout(() => {
              window.location = playStoreLink.value
            }, storeDelay)
          }
        }
      }
    })

    return {
      lang,
      appStoreLink,
      playStoreLink,
      appLogoTitle: themeConfig.app.logoTitle,
      onPrimaryClick,
      updateActiveLocale,
      year: new Date().getFullYear(),
      isChallenge,
    }
  },
}
</script>

<style lang="scss">
@import '@core/preset/preset/pages/landing.scss';

.matches-download-page {
  min-height: 76vh;
  display: flex;
  align-items: center;
  color: #ffffff;
  caret-color: #ffffff;
}

/* Mobile: increase min-height so content and bottom graphic fit better */
@media (max-width: 600px) {
  .matches-download-page {
    min-height: 82vh;
  }
}

.matches-page {
  /* make the root fill the viewport and use column flex layout so
     header + content + footer align properly and content stretches */
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

.matches-download-page {
  /* content area should grow to fill available space between header and footer */
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #222222;
}

.lang-switch v-btn {
  min-width: 0;
}

.download-cta {
  min-width: 260px;
}

.page-header {
  background-color: #000;
  color: #fff;
  padding: 12px 24px;
}

.page-header .v-btn {
  color: #fff !important;
}

.header-right .v-img {
  display: inline-block;
}

.session-image {
  min-height: 20vh;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  max-width: none;
  pointer-events: none;
  z-index: 2;
  /* ensure the image fills the available width */
  background-size: cover;
}

.app-image {
  max-width: 100%;
  max-height: 300px;
  width: auto;
  height: auto;
}

.footer {
  background-color: #000000 !important;
  padding: 30px 75px;
}

.footer-label {
  color: #fff;
  font-weight: 500;
}
</style>
